/* lib/planSections.js — тарифные флаги разделов «Аналитика» и «Интеграции».

   Бэкенд (subscriptions/sections.py) кладёт в КАЖДЫЙ объект пространства
   `sections: {analytics: bool, integrations: bool}`. Тариф выключает раздел ключом
   `false` в `SubscriptionPlan.features`, и тогда раздел закрыт для ВСЕГО пространства,
   включая владельца и админов (ролевые доступы — другой механизм, здесь не участвуют).

   Правило одно и такое же, как на сервере: раздел закрыт ТОЛЬКО при булевом `false`.
   Объекта `sections` нет (старый бэкенд, публичный демо-стенд, пространство ещё не
   загружено), нет ключа, null, 0, "false" — раздел ВИДЕН, ровно как до флагов. UI тут
   лишь убирает пункты, которые всё равно закрыл бы сервер; ошибись мы в сторону
   «закрыть», клиент потерял бы оплаченный раздел без всякой причины на бэкенде.

   Модуль чистый (без React/window) — каждое решение покрыто vitest. */

export const SECTION_ANALYTICS = 'analytics';
export const SECTION_INTEGRATIONS = 'integrations';

// Куда уводим с закрытого раздела: сразу к Ассистенту, без страницы «раздел отключён»
// (пункт меню исчез, а прямую ссылку из закладки/письма молча ведём в рабочий раздел).
export const SECTION_FALLBACK_PATH = '/chat';

/** Включён ли раздел у пространства. Выключает только булев false. */
export function isSectionEnabled(workspace, section) {
  const sections = workspace && workspace.sections;
  if (!sections || typeof sections !== 'object' || Array.isArray(sections)) return true;
  return sections[section] !== false;
}

/** Решение гарда маршрута: пустить или увести. redirectTo — только при allow=false. */
export function sectionRouteDecision(section, workspace) {
  return isSectionEnabled(workspace, section)
    ? { allow: true, redirectTo: null }
    : { allow: false, redirectTo: SECTION_FALLBACK_PATH };
}

/**
 * Какому разделу тарифа принадлежит адрес (null — никакому).
 * Узнаём: /analytics (+вложенные, query, hash), легаси /dashboard (редиректит на
 * /analytics), и /settings с подвкладкой «Интеграции» (&sub=integrations — одноразовый
 * deep-link, &biz=integrations — уже установленная подвкладка).
 */
export function sectionOfPath(path) {
  if (typeof path !== 'string' || !path) return null;
  const noHash = path.split('#')[0];
  const qIdx = noHash.indexOf('?');
  const pathname = (qIdx >= 0 ? noHash.slice(0, qIdx) : noHash).replace(/\/+$/, '') || '/';
  const query = qIdx >= 0 ? noHash.slice(qIdx + 1) : '';
  if (pathname === '/analytics' || pathname.startsWith('/analytics/') || pathname === '/dashboard') {
    return SECTION_ANALYTICS;
  }
  if (pathname === '/settings') {
    const q = new URLSearchParams(query);
    if (q.get('sub') === 'integrations' || q.get('biz') === 'integrations') return SECTION_INTEGRATIONS;
  }
  return null;
}

/** Ведёт ли адрес в раздел, который у пространства не закрыт. Не-тарифные адреса — всегда да. */
export function isPathAllowed(path, workspace) {
  const section = sectionOfPath(path);
  return !section || isSectionEnabled(workspace, section);
}

/**
 * Тексты шага онбординга для пространства с закрытыми разделами.
 * У шага может быть `copyWithout: { analytics: { body, primary, … } }` — поля варианта
 * накладываются на копию шага, если раздел выключен. Раздел включён (или sections нет) →
 * возвращаем ТОТ ЖЕ объект: стабильная ссылка не пересоздаёт эффекты оверлея.
 */
export function applySectionCopy(step, workspace) {
  const variants = step && step.copyWithout;
  if (!variants) return step;
  let out = step;
  Object.keys(variants).forEach((section) => {
    if (!isSectionEnabled(workspace, section)) out = { ...out, ...variants[section] };
  });
  return out;
}

/**
 * Отчёты по кассе (iiko/Syrve/r_keeper): «Аналитика» их показывает, «Интеграции» наполняют,
 * поэтому осмыслены только когда открыты ОБА раздела. От этого зависят тур по аналитике
 * (при закрытых интеграциях он велел бы «сначала подключите кассу» — звал бы в закрытый
 * раздел) и роли доступа команды.
 */
export function posAnalyticsAvailable(workspace) {
  return isSectionEnabled(workspace, SECTION_ANALYTICS)
    && isSectionEnabled(workspace, SECTION_INTEGRATIONS);
}

/**
 * Роли доступа команды (RoleEditorModal и т.п.) ограничивают вкладки аналитики iiko:
 * реестр lib/analyticsAccess.js целиком про них. Пока закрыта «Аналитика» или
 * «Интеграции», ограничивать нечего — и UI ролей в этом пространстве лишь напоминал бы
 * о разделе, которого у команды нет.
 */
export function rolesApplicable(workspace) {
  return posAnalyticsAvailable(workspace);
}

/**
 * Видит ли пользователь аналитику ЗАВЕДЕНИЯ хоть где-то — для мест, которые живут на уровне
 * пользователя, а не пространства (подсказка на личной вкладке тарифа, настройки
 * уведомлений). Правило: заведений нет вовсе (воронка «заведите заведение» — как было) или
 * хотя бы в одном из них «Аналитика» не закрыта тарифом. Все заведения закрыты → нет.
 */
export function userSeesBusinessAnalytics(workspaces) {
  const business = (workspaces || []).filter((w) => w && (w.kind === 'business' || w.is_business));
  return business.length === 0 || business.some((w) => isSectionEnabled(w, SECTION_ANALYTICS));
}

// Типы уведомлений, которые говорят ТОЛЬКО про аналитику заведения (данные кассы разобраны,
// «откройте Аналитику»). Бэкенд их закрытому пространству уже не шлёт; строку настройки
// прячем тому, кто аналитики заведения не видит нигде (userSeesBusinessAnalytics).
export const ANALYTICS_ONLY_NOTIFICATION_TYPES = ['onboarding_analytics_ready'];

/** Строки настроек уведомлений без аналитических типов для пользователя без аналитики. */
export function visiblePreferenceRows(rows, workspaces) {
  const list = rows || [];
  if (userSeesBusinessAnalytics(workspaces)) return list;
  return list.filter((p) => !ANALYTICS_ONLY_NOTIFICATION_TYPES.includes(p && p.type));
}
